import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type HorecaKitchenItemDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const NEXT: Record<string, { to: "PREPARING" | "READY" | "SERVED"; label: string }> = {
  SENT: { to: "PREPARING", label: "Počni" },
  PREPARING: { to: "READY", label: "Spremno" },
  READY: { to: "SERVED", label: "Odneto" },
};
const STATUS_LABEL: Record<string, string> = { SENT: "Novo", PREPARING: "U pripremi", READY: "Spremno" };
const STATUS_CLS: Record<string, string> = {
  SENT: "bg-amber-50 text-amber-800",
  PREPARING: "bg-blue-50 text-blue-800",
  READY: "bg-emerald-50 text-emerald-800",
};

function minutesAgo(iso: string | null) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
}

function describe(i: HorecaKitchenItemDto) {
  const c = i.choice ?? {};
  return [c.variation?.name, ...(c.addons ?? []).map((a) => "+ " + (a.name ?? "")), ...(c.without ?? []).map((w) => "bez " + w)].filter(Boolean).join(", ");
}

export function HorecaKuhinjaPage() {
  const qc = useQueryClient();
  const [stationId, setStationId] = useState("");
  const stations = useQuery({ queryKey: ["horeca-setup"], queryFn: () => api.getHorecaSetup(), retry: false });
  const board = useQuery({
    queryKey: ["horeca-kitchen", stationId],
    queryFn: () => api.getHorecaKitchen(stationId || undefined),
    refetchInterval: 5000,
    retry: false,
  });
  const set = useMutation({
    mutationFn: (v: { ids: string[]; status: "PREPARING" | "READY" | "SERVED" }) => api.setHorecaItemStatus(v.ids, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["horeca-kitchen"] }),
  });

  if (board.isError) {
    if (board.error instanceof ApiError && board.error.response.errorCode === "MODULE_NOT_AVAILABLE")
      return <ModuleGateNotice error={board.error} moduleKey="horeca" />;
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje nije uspelo.</p>;
  }
  const screenStations = (stations.data?.stations ?? []).filter((s) => s.is_active && s.output_mode !== "PRINTER");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Ekran kuhinje</h1>
          <p className="text-sm text-slate-500">Osvežava se samo na 5 sekundi. Isto vide Android i desktop aplikacija kuhinje.</p>
        </div>
        <select className="h-10 rounded-lg border border-border px-3 text-sm" value={stationId} onChange={(e) => setStationId(e.target.value)}>
          <option value="">Sve stanice</option>
          {screenStations.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      {!board.data && <Skeleton className="h-40 w-full" />}
      {board.data && board.data.tickets.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">Nema porudžbina za pripremu.</p>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {board.data?.tickets.map((t) => {
          const oldest = Math.max(...t.items.map((i) => minutesAgo(i.sent_at)));
          return (
            <div key={t.order_id} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-base font-semibold">Sto {t.table_label ?? "—"}</span>
                <span className={"text-xs " + (oldest >= 15 ? "font-semibold text-red-600" : "text-slate-500")}>{oldest} min</span>
              </div>
              {t.note && <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">{t.note}</p>}
              <div className="space-y-2">
                {t.items
                  .slice()
                  .sort((a, b) => a.course - b.course)
                  .map((i) => (
                    <div key={i.id} className="flex items-start justify-between gap-2 border-t border-border pt-2 first:border-0 first:pt-0">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{i.quantity} × {i.name} <span className="text-xs font-normal text-slate-400">hod {i.course}</span></div>
                        {describe(i) && <div className="text-xs text-slate-600">{describe(i)}</div>}
                        {i.choice?.note && <div className="text-xs italic text-slate-600">{i.choice.note}</div>}
                        <span className={"mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] " + (STATUS_CLS[i.status] ?? "")}>{STATUS_LABEL[i.status] ?? i.status}</span>
                      </div>
                      {NEXT[i.status] && (
                        <Button disabled={set.isPending} onClick={() => set.mutate({ ids: [i.id], status: NEXT[i.status]!.to })}>{NEXT[i.status]!.label}</Button>
                      )}
                    </div>
                  ))}
              </div>
              {t.items.some((i) => i.status === "SENT" || i.status === "PREPARING") && (
                <button className="mt-3 w-full rounded-lg border border-border py-1.5 text-sm hover:bg-slate-50" onClick={() => set.mutate({ ids: t.items.map((i) => i.id), status: "READY" })}>
                  Sve spremno
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
